import { FC, useState } from 'react';
import { Loading } from 'components/uikit/suspense/Loading';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';

import styles from './ManageRolesModal.module.scss';
import AddIcon from 'assets/img/add_icon.svg?react';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { QueryFactory } from 'services/api';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton.tsx';
import { ManageRoleModal } from '../manageRoleModal/ManageRoleModal.tsx';
import { RoleModalBlock } from '../roleModalBlock/RoleModalBlock.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';

type ManageRolesModalProps = {
  projectId: number;
  revisionId: number;
  onClose: () => void;
  isOpen: boolean;
};

export const ManageRolesModal: FC<ManageRolesModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.ManageRolesModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const [isManageRoleModalOpen, setIsManageRoleModalOpen] = useState(false);
  const [manageRoleModalId, setManageRoleModalId] = useState<
    number | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    revisionId: props.revisionId,
  });
  const modal = useModal();

  const projectRolesQuery = QueryFactory.ProjectRolesQuery.useSearchQuery(
    { projectId: props.projectId },
    { enabled: !!props.projectId && props.isOpen },
  );

  const deleteRole = async (id: number) => {
    const confirmed = await modal.showConfirm({
      title: i18n.t('delete_role_confirmation_title'),
      text: i18n.t('delete_role_confirmation_message'),
    });

    if (!confirmed) {
      return;
    }

    try {
      setIsLoading(true);
      await QueryFactory.ProjectRolesClient.delete_(id);

      await queryClient.invalidateQueries({
        queryKey: QueryFactory.ProjectRolesQuery.searchQueryKey(),
      });
      await invalidateSignatureBlockQueries();
    } catch (e) {
      showErrorToast(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-test-id={'manage-roles-modal'}>
      <CustomModal
        isOpen={props.isOpen}
        onClose={props.onClose}
        isBlocking={true}
        title={i18n.t('title')}
      >
        <Loading loading={isLoading || projectRolesQuery.isLoading}>
          <div className={styles.form}>
            <div className={styles.main}>
              <div className={styles.roles} data-test-id={'roles-list'}>
                {projectRolesQuery.data &&
                  projectRolesQuery.data.data.map((dto) => (
                    <RoleModalBlock
                      projectRole={dto}
                      onEdit={(id: number) => {
                        setManageRoleModalId(id);
                        setIsManageRoleModalOpen(true);
                      }}
                      onDelete={deleteRole}
                      key={dto.id}
                    />
                  ))}
              </div>
              <TextButton
                testId="add-new-role-button"
                className={styles.addButton}
                icon={<AddIcon className={styles.addIcon} />}
                title={i18n.t('add_new_role')}
                color={TextButtonColor.Primary}
                onClick={() => {
                  setIsManageRoleModalOpen(true);
                }}
              />
            </div>
            <div className={styles.footer}>
              <Button
                className={styles.button}
                color={ButtonColor.Secondary}
                title={buttonTitle.t('cancel')}
                onClick={props.onClose}
              />
              <Button
                className={styles.button}
                color={ButtonColor.Default}
                type={'submit'}
                title={buttonTitle.t('save')}
              />
            </div>
          </div>
        </Loading>
      </CustomModal>
      <ManageRoleModal
        projectId={props.projectId}
        revisionId={props.revisionId}
        projectRoleId={manageRoleModalId}
        onClose={() => {
          setIsManageRoleModalOpen(false);
          setManageRoleModalId(undefined);
        }}
        isOpen={isManageRoleModalOpen}
      />
    </div>
  );
};
